import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { cn } from './Table';
import { Thumb } from './Thumb';

// Select con BUSCADOR e IMAGEN por opción.
//
// Un <select> nativo solo acepta texto, así que para elegir un insumo entre cientos
// había que leer nombres. Este combobox muestra la miniatura al lado de cada opción
// (es mucho más rápido reconocer el material por su foto) y filtra escribiendo.
//
// Teclado: ↑/↓ mueven, Enter elige, Esc cierra. Click afuera cierra.
// z-index del panel: escala del proyecto (10/20/30/50) — acá 30, por encima de
// cabeceras sticky (20) y por debajo de modales (50).

export interface PickerOption {
    value: string | number;
    label: string;
    sublabel?: string;
    image?: string | null;
}

export function PickerSelect({
    options, value, onChange, placeholder = 'Seleccionar…', emptyText = 'Sin resultados',
    className, disabled, clearable,
}: {
    options: PickerOption[];
    value?: string | number | null;
    onChange: (value: string | number | null) => void;
    placeholder?: string;
    emptyText?: string;
    className?: string;
    disabled?: boolean;
    clearable?: boolean;
}) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState('');
    const [cursor, setCursor] = useState(0);
    const box = useRef<HTMLDivElement>(null);
    const input = useRef<HTMLInputElement>(null);

    const selected = useMemo(
        () => options.find(o => String(o.value) === String(value ?? '')),
        [options, value],
    );

    const filtered = useMemo(() => {
        const t = q.trim().toLowerCase();
        if (!t) return options;
        // match por nombre O por sublabel (ej: el tipo o el proveedor)
        return options.filter(o => `${o.label} ${o.sublabel || ''}`.toLowerCase().includes(t));
    }, [options, q]);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    useEffect(() => { if (open) { setQ(''); setCursor(0); setTimeout(() => input.current?.focus(), 0); } }, [open]);

    const pick = (o: PickerOption) => { onChange(o.value); setOpen(false); };

    const onKey = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(filtered.length - 1, c + 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor(c => Math.max(0, c - 1)); }
        else if (e.key === 'Enter') { e.preventDefault(); if (filtered[cursor]) pick(filtered[cursor]); }
        else if (e.key === 'Escape') { setOpen(false); }
    };

    // Miniaturas solo si alguna opción tiene imagen: una lista de proveedores con
    // iniciales en cada fila es ruido, no información.
    const withImages = options.some(o => o.image);

    return (
        // `tw`: tokens de uiarc + preflight acotado, así se ve igual en cualquier pantalla.
        <div ref={box} className={cn('tw relative', className)}>
            <button type="button" disabled={disabled} onClick={() => setOpen(o => !o)}
                className={cn(
                    // medidas y foco del `input` de uiarc: alto de control, borde fuerte, foco = borde
                    'flex h-[var(--control-height-md)] w-full items-center gap-2 rounded-[var(--radius-control)] border border-[var(--border-strong)] bg-card px-3 text-left text-[length:var(--text-sm)] text-ink',
                    'transition-colors hover:border-[var(--foreground)] focus:outline-none focus-visible:border-[var(--foreground)]',
                    disabled && 'cursor-not-allowed opacity-60',
                )}>
                {selected
                    ? <>
                        {withImages && <Thumb src={selected.image} name={selected.label} size="sm" />}
                        <span className="min-w-0 flex-1 truncate">
                            {selected.label}
                            {selected.sublabel && <span className="ml-1 text-faint">· {selected.sublabel}</span>}
                        </span>
                    </>
                    : <span className="min-w-0 flex-1 truncate text-faint">{placeholder}</span>}
                {clearable && selected && (
                    <X size={13} className="shrink-0 text-faint hover:text-ink"
                        onClick={(e) => { e.stopPropagation(); onChange(null); }} />
                )}
                <ChevronDown size={14} className="shrink-0 text-faint" />
            </button>

            {open && (
                <div className="absolute left-0 right-0 z-30 mt-1 min-w-[16rem] overflow-hidden rounded-[var(--radius-panel)] border border-line bg-card shadow-[var(--shadow-floating)] animate-in fade-in-0 zoom-in-95">
                    <div className="flex items-center gap-2 border-b border-line px-3 py-2">
                        <Search size={13} className="shrink-0 text-faint" />
                        <input ref={input} value={q} onChange={e => { setQ(e.target.value); setCursor(0); }} onKeyDown={onKey}
                            placeholder="Buscar…"
                            className="w-full bg-transparent text-[length:var(--text-sm)] text-ink placeholder:text-faint focus:outline-none" />
                    </div>
                    <div className="max-h-72 overflow-y-auto p-1">
                        {filtered.length === 0 && <div className="px-3 py-4 text-center text-[length:var(--text-sm)] text-faint">{emptyText}</div>}
                        {filtered.map((o, i) => (
                            <button key={o.value} type="button" onMouseEnter={() => setCursor(i)} onClick={() => pick(o)}
                                className={cn(
                                    'flex w-full items-center gap-2 rounded-[calc(var(--radius-control)-2px)] px-2 py-1.5 text-left text-[length:var(--text-sm)] text-ink',
                                    i === cursor && 'bg-muted',
                                    String(o.value) === String(value ?? '') && 'font-semibold',
                                )}>
                                {withImages && <Thumb src={o.image} name={o.label} size="sm" />}
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate">{o.label}</span>
                                    {o.sublabel && <span className="block truncate text-[length:var(--text-xs)] text-faint">{o.sublabel}</span>}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
